import { Button, IconButton, Tooltip } from "@mui/material";
import { Dispatch, Fragment, SetStateAction, useContext, useEffect, useMemo, useState } from "react";
import { FormFieldContext } from "contexts/FormFieldContext";
import { CaseFormContext } from "contexts/CaseFormContext";
import { useAppSelector } from "redux/hooks";
import { selectForm, selectFormFields, selectFormSections } from "redux/slices/form.slice";
import { selectCase, selectCaseInfo } from "redux/slices/case";
import { useSnackbar } from "notistack";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import { Navigate, useNavigate } from "react-router";
import {
  Close,
  Delete,
  Edit,
  FindInPage,
  KeyboardArrowDown,
  KeyboardArrowUp,
  MenuOpenOutlined,
} from "@mui/icons-material";
import FormFieldValueGroupEditor from "components/FormField/FormFieldValueGroupEditor";
import displayCellValue from "components/FormField/displayCellValue";
import ValueGroupNotes from "components/FormField/ValueGroupNotes";
import { FieldValueGroupReferences } from "components/FormField/FieldValueGroupReferenceCard";
import { Block } from "generated/graphql";
import CaseFormIntake from "../../CaseFormIntake";
import useSearchParamsWithUpdate from "helpers/hooks/useSearchParamsWithUpdate";
import { parseSafeInt, RowViewState, UNGROUPED_CELL_ID } from "Backend";
import { AppRoutes } from "AppRoutes";
import SourceTypeIcon from "components/FormField/SourceTypeIcon";
import DescriptionTooltip from "components/FormField/DescriptionTooltip";
import { EntryContext } from "components/tags/types";
import { useFilteredValueGroups } from "helpers/hooks/useFilteredValueGroups";
import { ValueGroupStar } from "components/FormField/ValueGroupStar";
import { TagDisplay } from "components/BetterResponseTable/TagDisplay";
import { ValueGroupNavigationPanel } from "components/FormField/ValueGroupNavigationPanel";
import { cn } from "components/lib/utils";
import { pluralize } from "helpers/coll";
import CaseInfoHeader from "components/CaseInfoHeader";
import ButtonSummary from "../../ButtonSummary";

// Nullary component for the router, handles redirects from old URL
// /<lensId>/cases/<caseId>/profile/<fieldId>/responses/<responseId>
// so we don't break links
export function EntryViewerShim() {
  const { searchParams } = useSearchParamsWithUpdate();
  const { fieldId, caseId, lensId, valueGroupId } = useParamNumbers(["fieldId", "caseId", "lensId", "valueGroupId"]);
  const referenceId = parseSafeInt(searchParams.get("refId") || searchParams.get("referenceId"));
  const page = parseSafeInt(searchParams.get("page"));

  if (!fieldId || !caseId || !lensId) return <Navigate to=".." />;

  return (
    <Navigate
      to={AppRoutes.caseProfileSection({
        caseId,
        lensId,
        fieldId,
        valueGroupId,
        referenceId,
        page,
      })}
    />
  );
}

export interface EntryViewerProps {
  entryContext: EntryContext;
  setEntryContext: Dispatch<SetStateAction<EntryContext | undefined>>;
  onClose: () => void;
  // For calc cards, we don't update query string params in entry viewer so links don't work
  // This flag tells us to not look at the query string in an attempt to support the back button
  isLinkable: boolean;
}

export function EntryViewer({ entryContext, setEntryContext, onClose, isLinkable }: EntryViewerProps) {
  const { caseId, lensId } = useParamNumbers(["caseId", "lensId"]);
  const formFieldContext = useContext(FormFieldContext);
  const { filteredValueGroups, groupedAndFilteredValueGroups } = useFilteredValueGroups();
  const [editMode, setEditMode] = useState(false);
  const isGrouped = formFieldContext.groupByCellIdHandle[0] !== UNGROUPED_CELL_ID;
  const [collapsePanels, setCollapsePanels] = useState(formFieldContext.valueGroups.length <= 1);
  const form = useAppSelector(selectForm);
  const caseForm = useContext(CaseFormContext);
  const fields = useAppSelector(selectFormFields);
  const sections = useAppSelector(selectFormSections);
  const caseInfo = useAppSelector(selectCaseInfo);
  const lensCase = useAppSelector(selectCase);
  const { searchParams } = useSearchParamsWithUpdate();

  const { valueGroupId, referenceId, page } = entryContext;
  const fieldId = formFieldContext.formField.id;

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const field = fields.find((f) => f.id === fieldId);
  const section = sections.find((s) => s.id === field?.sectionId);
  const group = formFieldContext.valueGroups.find((vg) => vg.id === valueGroupId);
  const activeReference = useMemo(
    () => group?.references.find((r) => r.id === referenceId) || group?.references[0],
    [referenceId, group?.references]
  );
  // Side effect: if you delete a reference, we have to clean up reference id in the URL params
  // It will select the next available one, if you delete the last reference, it will close the modal
  // Can remove this by threading setEntryContext through FieldValueGroupReferences and updating the callsites
  useEffect(() => {
    setEntryContext((current) => {
      if (!current) return;

      return { ...current, referenceId: activeReference?.id, page: undefined };
    });
  }, [activeReference && referenceId !== activeReference?.id]);

  // Side effect: Detect when query string changes and close/reset the modal if it doesn't match up
  // with the entry context (happens when hitting Back/Forward)
  useEffect(() => {
    const fieldId = parseSafeInt(searchParams.get("fieldId"));
    const valueGroupId = parseSafeInt(searchParams.get("valueGroupId"));
    const entryContextFromParams = {
      valueGroupId: valueGroupId === undefined ? -1 : valueGroupId,
      referenceId: parseSafeInt(searchParams.get("referenceId")),
      page: parseSafeInt(searchParams.get("page")),
    } as EntryContext;
    const shouldClose =
      isLinkable &&
      (formFieldContext.formField.id !== fieldId || entryContextFromParams.valueGroupId !== entryContext.valueGroupId);
    if (shouldClose) setEntryContext(entryContextFromParams.valueGroupId === -1 ? undefined : entryContextFromParams);
  }, [searchParams]);

  const currentPage = page || activeReference?.originalPage || 1;

  const selectNewResponse = (newValueGroupId: number) => {
    const valueGroup = formFieldContext.valueGroupById(newValueGroupId);
    if (valueGroup) {
      formFieldContext.setValueGroupViewState(valueGroupId, RowViewState.ReadCollapsed);
      formFieldContext.setValueGroupViewState(newValueGroupId, RowViewState.ReadExpanded);
      setEntryContext({ valueGroupId: newValueGroupId, referenceId: valueGroup.references[0]?.id });
    }
  };
  const flattenedList = useMemo(() => {
    if (isGrouped) {
      return groupedAndFilteredValueGroups.flatMap((group) => group.valueGroups);
    } else {
      return filteredValueGroups;
    }
  }, [isGrouped, filteredValueGroups, groupedAndFilteredValueGroups]);
  const currentIndex = useMemo(
    () => flattenedList.findIndex((vg) => vg.id === entryContext.valueGroupId),
    [entryContext.valueGroupId, flattenedList]
  );

  const selectPrevious = () => {
    const prevVg = flattenedList[currentIndex - 1];
    if (!prevVg) return;
    return selectNewResponse(prevVg.id);
  };

  const selectNext = () => {
    const nextVg = flattenedList[currentIndex + 1];
    if (!nextVg) return;
    return selectNewResponse(nextVg.id);
  };

  const deleteResponse = async () => {
    if (group !== undefined) {
      try {
        const nextValueGroup = formFieldContext.getNextValueGroup(valueGroupId);
        const firstValueGroup = formFieldContext.valueGroups[0];
        formFieldContext.removeValueGroup(group.id, async () => {
          if (nextValueGroup) {
            selectNewResponse(nextValueGroup.id);
          } else if (firstValueGroup) {
            selectNewResponse(firstValueGroup.id);
          }
        });
      } catch (e) {
        enqueueSnackbar("Unable to delete entry", { variant: "error" });
      }
    }
  };

  const immediatePinToValueGroup = async (block: Block) => {
    const intakeId = activeReference?.documentIntakeId;
    if (intakeId !== undefined && form !== undefined && valueGroupId !== undefined && currentPage !== undefined) {
      await formFieldContext.addReferenceToValueGroup(valueGroupId, intakeId, currentPage, block);
      enqueueSnackbar("Reference added", { variant: "success" });
    }
  };

  const cancelEdits = () => {
    caseForm.resetFieldLocal(fieldId);
    setEditMode(false);
  };

  const saveEdits = async () => {
    try {
      // Force errors to display in case the value is invalid
      formFieldContext.setValueGroupErrors(valueGroupId);

      const didUpdate = !!(await formFieldContext.saveValueGroup(valueGroupId));
      didUpdate && setEditMode(false);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    caseForm.focusFieldReferenceState[1](referenceId);
    caseForm.intakeViewerBlockIndexState[1](activeReference?.blockIndex);
    caseForm.focusFieldValueGroupState[1]({
      fieldId,
      valueGroupId: valueGroupId,
    });

    return () => {
      caseForm.focusFieldReferenceState[1](undefined);
      caseForm.intakeViewerBlockIndexState[1](undefined);
    };
  }, [referenceId, activeReference?.blockIndex, valueGroupId]);

  // Only fire this once for page load. Afterwards, this is handled by the onChange on the Response dropdown
  useEffect(() => {
    if (group !== undefined) {
      // Expand this response when you go back to profile
      formFieldContext.setValueGroupViewState(group.id, RowViewState.ReadExpanded);
    }
  }, [group === undefined]);

  if (lensId === undefined || caseId === undefined || field === undefined || form === undefined) return null;

  if (group === undefined) {
    return (
      <div className="flex items-center flex-col justify-start h-full gap-4 pl-2 mt-4">
        <p className="font-semibold text-xs">No Entries</p>
        <Button
          color="default"
          variant="contained"
          onClick={() => navigate(AppRoutes.caseDetailBuilder(lensId, caseId, "profile"))}>
          Back to Profile
        </Button>
      </div>
    );
  }

  const referencePageCount = group.references.reduce((acc, obj) => acc.add(obj.originalPage), new Set()).size;

  return (
    <div className="flex flex-col w-full overflow-hidden text-gray-900">
      <div className="border-b border-gray-100 shadow flex items-center justify-between pr-4 h-12">
        <div className="p-4 flex items-center text-sm gap-2">
          <span className="font-bold">{section?.name}</span>
          <span className="text-gray-100">/</span>
          <span className="font-bold inline-flex items-center gap-1">
            {field.outlinePrefix} {field.name}
            <DescriptionTooltip description={field.description} />
          </span>
        </div>

        <div className="flex items-center">
          <CaseInfoHeader mycase={lensCase} />
          <ButtonSummary />
          <IconButton onClick={() => onClose()} size="small">
            <Close />
          </IconButton>
        </div>
      </div>
      <div data-pendo="focus-mode" className="flex h-full overflow-hidden overflow-y-auto pl-1">
        <div className={cn(collapsePanels && "hidden", "min-w-40 max-h-full overflow-y-auto mt-2")}>
          <div className="sticky top-0 px-2 pb-2 flex items-center bg-white z-10">
            <IconButton
              className="h-5 w-5"
              disableRipple
              disabled={currentIndex === 0}
              onClick={() => selectPrevious()}
              title="Previous Entry">
              <KeyboardArrowUp />
            </IconButton>
            <IconButton
              className="h-5 w-5"
              disableRipple
              disabled={currentIndex === flattenedList.length - 1}
              onClick={() => selectNext()}
              title="Next Entry">
              <KeyboardArrowDown />
            </IconButton>
            <div className="grow" />
            <IconButton className="h-5 w-5" onClick={() => setCollapsePanels(!collapsePanels)}>
              <MenuOpenOutlined />
            </IconButton>
          </div>
          <ValueGroupNavigationPanel
            groupId={group.id}
            valueGroups={filteredValueGroups}
            setGroupId={({ groupId }) => groupId !== undefined && selectNewResponse(groupId)}
            groupResponseNumber={group.responseNumber}
          />
        </div>
        <div className={cn(!collapsePanels ? "hidden" : "flex flex-col gap-2 items-center w-8 pt-4")}>
          <IconButton className="h-5 w-5 scale-x-[-1]" onClick={() => setCollapsePanels(!collapsePanels)}>
            <MenuOpenOutlined />
          </IconButton>
          <IconButton
            className="h-5 w-5"
            disableRipple
            disabled={currentIndex === 0}
            onClick={() => selectPrevious()}
            title="Previous Entry">
            <KeyboardArrowUp />
          </IconButton>
          <IconButton
            className="h-5 w-5"
            disableRipple
            disabled={currentIndex === flattenedList.length - 1}
            onClick={() => selectNext()}
            title="Next Entry">
            <KeyboardArrowDown />
          </IconButton>
        </div>
        <div
          data-pendo={`response-card-${valueGroupId}`}
          className={cn("flex flex-col pl-2 pr-1 w-72 border-l border-gray-50", collapsePanels && "w-96")}>
          <div className="flex items-center justify-between">
            <div className="flex items-center mb-2 pt-2 gap-2">
              <ValueGroupStar group={group} sx={{ width: 20, height: 20 }} />
              <h1 className="font-bold min-w-16">Entry #{group.responseNumber}</h1>
              {group?.source && (
                <SourceTypeIcon
                  valueGroupSource={group.source}
                  formField={formFieldContext.formField}
                  valueGroup={group}
                  sx={{
                    width: 20,
                    height: 20,
                  }}
                />
              )}
              <Tooltip title="Delete Entry">
                <IconButton size="small" onClick={() => deleteResponse()} data-pendo="reference-viewer-delete-response">
                  <Delete />
                </IconButton>
              </Tooltip>
            </div>

            {!editMode && (
              <IconButton size="small" onClick={() => setEditMode(true)} data-pendo="reference-viewer-edit-response">
                <Edit />
              </IconButton>
            )}
          </div>
          {group.availableTags.length > 0 && (
            <div className="pb-2">
              <TagDisplay
                valueGroupSerialId={group.serialId}
                appliedTags={group.tags}
                availableTags={group.availableTags}
                tagSize="xs"
              />
            </div>
          )}
          <div className="overflow-y-auto">
            <div className="flex items-center justify-between"></div>
            <div className="flex flex-col gap-2 pr-2 mt-2">
              {editMode ? (
                <div className="flex flex-col gap-2 pt-4">
                  <FormFieldValueGroupEditor
                    cells={formFieldContext.formField.cells}
                    groupId={valueGroupId}
                    values={group.values}
                    caseInfo={caseInfo}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-2 items-baseline gap-y-1">
                  {group.values.map((v, cellIndex) => {
                    const cell = formFieldContext.formField.cells[cellIndex];
                    const valueDisplay = displayCellValue(v.value, caseInfo);
                    if (cell === undefined) return null;
                    return (
                      <Fragment key={cellIndex}>
                        <div className="flex items-center gap-1">
                          <p title={cell.name} className="font-semibold text-xs break-words">
                            {cell.name}:
                          </p>
                          <DescriptionTooltip description={cell.description} />
                        </div>
                        <p className="pl-2 self-stretch text-xs">{valueDisplay}</p>
                      </Fragment>
                    );
                  })}
                </div>
              )}
              {(group.notes || editMode) && (
                <>
                  <p className="text-xxs font-semibold mt-2">Notes</p>
                  {editMode ? (
                    <ValueGroupNotes group={group} />
                  ) : (
                    <p className="text-xxs whitespace-pre-line">{group.notes}</p>
                  )}
                </>
              )}
              {editMode && (
                <div className="flex items-center gap-2 justify-end">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={cancelEdits}
                    data-pendo="reference-viewer-cancel-edit">
                    Cancel
                  </Button>
                  <Button
                    disabled={caseForm.mutationInProgressHandle[0]}
                    size="small"
                    variant="contained"
                    onClick={saveEdits}
                    data-pendo="reference-viewer-save-edit">
                    Save
                  </Button>
                </div>
              )}
            </div>
            <h6 className="text-xs mt-8 mb-2">
              <span className="text-base font-bold">References </span>(
              {referencePageCount + " " + pluralize(referencePageCount, "page")})
            </h6>
            {group.references.length === 0 && <p className="text-gray-300 text-xs italic">No references</p>}
            <FieldValueGroupReferences
              onClick={setEntryContext}
              valueGroup={group}
              dataPendo="reference-viewer-references-list"
            />
          </div>
        </div>
        {/*  overflow-x-auto stops the doc viewer from getting in some feedback loop due to not enough horiz space */}
        <div className="pl-2 grow overflow-x-auto h-full">
          {activeReference?.documentIntakeId ? (
            <CaseFormIntake
              className="rounded-none"
              lensId={lensId}
              caseId={caseId}
              intakeId={activeReference.documentIntakeId}
              onFormUpdate={caseForm.resetAllFieldsServer}
              defaultShowHighlightLensHits={false}
              immediatePinToValueGroup={immediatePinToValueGroup}
              blockIndex={activeReference.blockIndex}
              pageNumState={[
                currentPage,
                (value) =>
                  setEntryContext((current) => {
                    if (!current) return;
                    return { ...current, page: value };
                  }),
              ]}
            />
          ) : (
            <div className="flex flex-col w-full h-full items-center justify-center text-gray-400">
              <FindInPage className="h-16 w-16" />
              <span>This entry has no references</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
